
import { defineComponent, PropType } from 'vue';
import { PostDto, SimpleCommentDto } from '@/api/models/blog.dtos';
import AccountProfileImageButton from '@/components/accounts/AccountProfileImageButton.vue';
import dayjs from 'dayjs';
import store from '@/store';
import {
  createCommentToComment,
  createGuestCommentToComment,
  deleteComment,
  getComment,
  modifyComment
} from '@/api/blog';
import { HttpApiError } from '@/api/common/httpApiClient';

export default defineComponent({
  name: 'SimpleCommentItem',
  components: { AccountProfileImageButton },
  props: {
    simpleComment: Object as PropType<SimpleCommentDto>,
    post: Object as PropType<PostDto>,
  },
  data() {
    return {
      content: '',
      newComment: '',
      guestCommentName: '',
      guestCommentPassword: '',
      isNestedCommentsOpened: false,
      nestedComments: {} as Array<SimpleCommentDto>,
      isNestedCommentsLoaded: false,
    };
  },
  watch: {
    simpleComment() {
      this.content = this.simpleComment?.content ?? '';
    },
  },
  computed: {
    longCreatedAt() {
      return dayjs(this.simpleComment?.createdAt).format('YYYY.MM.DD H:mm');
    },
    isValidNewComment() {
      return this.newComment.length > 0;
    },
    isMyComment() {
      return this.simpleComment?.author.blogId === store.state.accountStore.blogId;
    },
    isGuestComment() {
      return this.simpleComment?.author?.guest;
    },
    isMyPost() {
      return this.post?.author.blogId === store.state.accountStore.blogId;
    },
    isLoggedIn(): boolean {
      return store.getters['accountStore/isLoggedIn'] ?? false;
    },
    nestedCommentsCount(): number {
      return this.isNestedCommentsLoaded ? this.nestedComments.length ?? 0 : this.simpleComment?.childrenCount ?? 0;
    }
  },
  methods: {
    async modifyComment() {
      if (!this.simpleComment?.id) {
        return;
      }
      const id = this.simpleComment.id;
      const newContent = prompt('수정할 내용을 입력하세요.', this.simpleComment.content);
      if (!newContent) {
        return;
      }
      const password = this.isGuestComment ? prompt("비밀번호를 입력하세요.") : null;
      if (this.isGuestComment && !password) {
        return;
      }

      await modifyComment(id, {
        content: newContent,
      }, password)
      .then((comment) => {
        alert('댓글을 수정했습니다.');
        this.content = comment.content;
        this.onCommentChanged();
      })
      .catch((error: HttpApiError) => {
        alert(error.getErrorMessage());
      });
    },
    async deleteComment() {
      if (!this.simpleComment?.id || !confirm('댓글을 삭제하시겠습니까?')) {
        return;
      }
      const id = this.simpleComment.id;
      const passwordRequired = this.isGuestComment && !this.isMyPost;
      const password = passwordRequired ? prompt("비밀번호를 입력하세요.") : null;
      if (passwordRequired && !password) {
        return;
      }

      await deleteComment(id, password)
      .then(() => {
        this.$emit('deleted', id);
      })
      .catch((error: HttpApiError) => {
        alert(error.getErrorMessage());
      });
    },
    async writeComment() {
      if (!this.simpleComment?.id) {
        return;
      }

      if(this.isLoggedIn) {
        await createCommentToComment(this.simpleComment.id, {
          content: this.newComment,
        })
        .then((comment) => {
          this.newComment = '';
          this.nestedComments.push(Object.assign(comment, {
            childrenCount: comment.children.length
          }));
          this.onCommentChanged();
        })
        .catch((error: HttpApiError) => {
          alert(error.getErrorMessage());
        });
      } else {
        await createGuestCommentToComment(this.simpleComment.id, {
          content: this.newComment,
          name: this.guestCommentName,
          password: this.guestCommentPassword
        })
        .then((comment) => {
          console.log(comment);
          this.newComment = '';
          this.guestCommentPassword = '';
          this.nestedComments.push(Object.assign(comment, {
            childrenCount: comment.children.length
          }));
          this.onCommentChanged();
        })
        .catch((error: HttpApiError) => {
          alert(error.getErrorMessage());
        });
      }
    },
    async toggleNestedReplies() {
      if (!this.simpleComment?.id) {
        return;
      }

      this.isNestedCommentsOpened = !this.isNestedCommentsOpened;
      if (this.isNestedCommentsOpened && !this.isNestedCommentsLoaded) {
        await getComment(this.simpleComment.id)
        .then((comment) => {
          this.nestedComments = comment.children;
          this.isNestedCommentsLoaded = true;
        })
        .catch((error: HttpApiError) => {
          alert(error.getErrorMessage());
          this.isNestedCommentsOpened = !this.isNestedCommentsOpened;
        });
      }
    },
    commentDeleted(commentId: string) {
      this.nestedComments = this.nestedComments.filter((x) => x.id !== commentId);
      this.onCommentChanged();
    },
    onCommentChanged() {
      if (!this.simpleComment) {
        return;
      }

      this.$emit('changed', {
        id: this.simpleComment.id,
        content: this.content,
        parentId: this.simpleComment.parentId,
        childrenCount: this.nestedComments.length,
        author: this.simpleComment.author,
        createdAt: this.simpleComment.createdAt
      } as SimpleCommentDto);
    },
  },
  created() {
    this.content = this.simpleComment?.content ?? '';
  },
});
